import { StatusIndicatorProps } from "@amzn/awsui-components-react/polaris/status-indicator";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter";
import {
  Investigation,
  InvestigationFieldBreakdown,
  InvestigationInboxItem,
  InvestigationOverlap,
  InvestigationPredicate,
  InvestigationSample,
  InvestigationSuggestion,
  Stage,
  TrackStarsReportSummary,
} from "../../lusca-search-api/generated-src";
import { GroupsFilterQuery, serializeGroupsFilter } from "./groupsFilter";

export type InvestigationRule = Array<InvestigationPredicate>;

/**
 * PropertyFilter labels shared by the investigation tables so both filter bars read the same;
 * only the aria label and placeholder differ per table.
 */
export const propertyFilterI18n = (
  filteringAriaLabel: string,
  filteringPlaceholder: string
): PropertyFilterProps.I18nStrings => ({
  filteringAriaLabel,
  filteringPlaceholder,
  dismissAriaLabel: "Dismiss",
  groupValuesText: "Values",
  groupPropertiesText: "Properties",
  operatorsText: "Operators",
  operationAndText: "and",
  operationOrText: "or",
  operatorLessText: "Less than",
  operatorLessOrEqualText: "Less than or equal",
  operatorGreaterText: "Greater than",
  operatorGreaterOrEqualText: "Greater than or equal",
  operatorContainsText: "Contains",
  operatorDoesNotContainText: "Does not contain",
  operatorEqualsText: "Equals",
  operatorDoesNotEqualText: "Does not equal",
  editTokenHeader: "Edit filter",
  propertyText: "Property",
  operatorText: "Operator",
  valueText: "Value",
  cancelActionText: "Cancel",
  applyActionText: "Apply",
  allPropertiesLabel: "All properties",
  tokenLimitShowMore: "Show more",
  tokenLimitShowFewer: "Show fewer",
  clearFiltersText: "Clear filters",
  removeTokenButtonAriaLabel: (token) =>
    `Remove filter ${token.propertyKey ?? ""} ${token.operator} ${token.value}`,
  enteredTextLabel: (text) => `Use "${text}"`,
});

/** Comparison operators offered on numeric PropertyFilter properties. */
export const NUMERIC_FILTER_OPERATORS: PropertyFilterProps.ComparisonOperator[] =
  ["=", "!=", ">", ">=", "<", "<="];

/** Key detail shape shared by Cloudscape Textarea/Input onKeyDown events. */
interface ModEnterKeyDetail {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
}

/**
 * onKeyDown handler for multi-line fields: Cmd+Enter (macOS) or Ctrl+Enter submits,
 * plain Enter keeps inserting newlines. `submit` is skipped while `disabled` so the
 * shortcut mirrors the button's enabled state exactly.
 */
export const submitOnModEnter =
  (submit: () => void, disabled = false) =>
  ({ detail }: { detail: ModEnterKeyDetail }) => {
    if (detail.key !== "Enter" || !(detail.metaKey || detail.ctrlKey)) return;
    if (!disabled) submit();
  };

/**
 * User-facing wording for a case with no owner (API/storage still say "unclaimed").
 * Distinct from "uninvestigated", which describes DLQ items no case covers at all.
 */
export const NEEDS_OWNER_LABEL = "Needs an owner";

/** "Owner alias" or the needs-an-owner label, for badges beside a case title. */
export const ownerLabel = (owner?: string | null): string =>
  owner ? `Owner ${owner}` : NEEDS_OWNER_LABEL;

/** Seed values carried from a dashboard row (QuickSight callback or manual entry). */
export interface RowSeed {
  dlqPk: string;
  eventsourcekey?: string;
  error_type?: string;
  error_message?: string;
  error_category?: string;
  gl_product_group?: string;
  marketplace_id?: string;
  usecase?: string;
  digital_order_source?: string;
  is_emp_order?: string;
  destination?: string;
  accounting_operation?: string;
  payment_processor?: string;
  exception_fingerprint?: string;
  flare_error_types?: string;
  flare_invalid_values?: string;
  flare_missing_attributes?: string;
  flare_failed_schema_definitions?: string;
  flare_attributes_not_covered?: string;
  flare_balance_scope?: string;
  flare_balance_scope_value?: string;
  /** Display-only: the item's transaction / order ID for a Lusca Search link. Never a rule field. */
  transaction_id?: string;
  order_id?: string;
}

export interface RuleFieldDefinition {
  field: InvestigationPredicate["field"];
  label: string;
  defaultSelected: boolean;
  /** Boolean snapshot column: value must be "true" or "false" (mirrors backend BOOLEAN_RULE_FIELDS). */
  boolean?: boolean;
}

export const BOOLEAN_RULE_OPTIONS = [
  { value: "true", label: "true" },
  { value: "false", label: "false" },
];

/** Allowlisted, bounded snapshot fields (must match the backend allowlist). */
export const RULE_FIELDS: RuleFieldDefinition[] = [
  {
    field: "eventsourcekey" as InvestigationPredicate["field"],
    label: "Queue / event source",
    defaultSelected: true,
  },
  {
    field: "error_type" as InvestigationPredicate["field"],
    label: "Error type",
    defaultSelected: true,
  },
  {
    field: "error_message" as InvestigationPredicate["field"],
    label: "Templated error message",
    defaultSelected: true,
  },
  {
    field: "error_category" as InvestigationPredicate["field"],
    label: "Error category",
    defaultSelected: true,
  },
  {
    field: "gl_product_group" as InvestigationPredicate["field"],
    label: "GL product group",
    defaultSelected: false,
  },
  {
    field: "marketplace_id" as InvestigationPredicate["field"],
    label: "Marketplace",
    defaultSelected: false,
  },
  {
    field: "usecase" as InvestigationPredicate["field"],
    label: "CBE use case",
    defaultSelected: false,
  },
  {
    field: "digital_order_source" as InvestigationPredicate["field"],
    label: "Digital order source",
    defaultSelected: false,
  },
  {
    field: "is_emp_order" as InvestigationPredicate["field"],
    label: "EMP (externally managed payment) order",
    defaultSelected: false,
    boolean: true,
  },
  {
    field: "destination" as InvestigationPredicate["field"],
    label: "Destination (FLARE/RACE)",
    defaultSelected: false,
  },
  {
    field: "accounting_operation" as InvestigationPredicate["field"],
    label: "Accounting operation",
    defaultSelected: false,
  },
  {
    field: "payment_processor" as InvestigationPredicate["field"],
    label: "Payment processor",
    defaultSelected: false,
  },
  // Stack-trace diagnostics from the DLQ Glue job (empty on snapshots that predate them).
  {
    field: "exception_fingerprint" as InvestigationPredicate["field"],
    label: "Failing code (exception and method)",
    defaultSelected: false,
  },
  {
    field: "flare_error_types" as InvestigationPredicate["field"],
    label: "FLARE error types",
    defaultSelected: false,
  },
  {
    field: "flare_invalid_values" as InvestigationPredicate["field"],
    label: "FLARE rejected values",
    defaultSelected: false,
  },
  {
    field: "flare_missing_attributes" as InvestigationPredicate["field"],
    label: "FLARE missing attributes",
    defaultSelected: false,
  },
  {
    field: "flare_failed_schema_definitions" as InvestigationPredicate["field"],
    label: "FLARE failed schemas",
    defaultSelected: false,
  },
  {
    field: "flare_attributes_not_covered" as InvestigationPredicate["field"],
    label: "FLARE attributes not covered",
    defaultSelected: false,
  },
  {
    field: "flare_balance_scope" as InvestigationPredicate["field"],
    label: "FLARE unbalanced scope",
    defaultSelected: false,
  },
  {
    field: "flare_balance_scope_value" as InvestigationPredicate["field"],
    label: "FLARE unbalanced scope value",
    defaultSelected: false,
  },
];

/** Most values one `in` predicate may list (mirrors the API's InvestigationPredicate.values). */
export const MAX_RULE_VALUES = 50;

/** The category value the builder and the backlog use for "the categoriser has not named it yet". */
export const UNCATEGORIZED = "uncategorized";

/**
 * True for a `not_in` predicate. Lusca writes these when a rule created with
 * `error_category = uncategorized` is stored (the pin becomes "none of the categories the pattern
 * already had", so the case keeps tracking its items once the categoriser names them), and the
 * builder adds them to leave out the items other cases own (see `ownedExclusion`).
 */
export const isExclusion = (p: InvestigationPredicate): boolean =>
  (p.operator as string) === "not_in";

/**
 * The value set a predicate binds its field to: `[value]` for `eq`, `values` for `in`. Every
 * rule consumer goes through this so a multi-valued field is never read as a single string.
 * A `not_in` predicate binds no finite set, so it has none — see `excludedValues`.
 */
export function predicateValues(p: InvestigationPredicate): string[] {
  if (isExclusion(p)) return [];
  const raw = p.operator === "in" ? p.values ?? [] : [p.value ?? ""];
  return raw.filter((v) => v !== "");
}

/** The values a `not_in` predicate rules out (empty for `eq` / `in`). */
export const excludedValues = (p: InvestigationPredicate): string[] =>
  isExclusion(p) ? (p.values ?? []).filter((v) => v !== "") : [];

/** True when the predicate accepts `value` (booleans compared case-insensitively). */
export function predicateAccepts(
  p: InvestigationPredicate,
  value: string
): boolean {
  if (isExclusion(p)) return !excludedValues(p).includes(value);
  const isBoolean = RULE_FIELDS.some((f) => f.field === p.field && f.boolean);
  const wanted = isBoolean ? value.toLowerCase() : value;
  return predicateValues(p).some((v) =>
    isBoolean ? v.toLowerCase() === wanted : v === wanted
  );
}

/**
 * The canonical predicate for a field and its value set (mirrors the backend's make_predicate):
 * `eq` for one value, `in` for several — sorted and deduplicated so equal sets serialise equally.
 * Returns null for an empty set (the field is not part of the rule).
 */
export function makePredicate(
  field: InvestigationPredicate["field"],
  values: string[]
): InvestigationPredicate | null {
  const distinct = Array.from(new Set(values.filter((v) => v !== ""))).sort();
  if (!distinct.length) return null;
  if (distinct.length === 1)
    return { field, operator: "eq", value: distinct[0] };
  return { field, operator: "in", values: distinct };
}

/**
 * "Exclude items owned by other cases": the predicates that, applied to the draft rule, leave out
 * every active overlap that offers a carve-out (the backend's InvestigationOverlap.exclusion), with
 * how many previewed items those cases hold. Carve-outs on the same field combine — `not_in` lists
 * union, `in` sets intersect — and a field with both kinds, or an empty intersection, is dropped
 * with the cases that asked for it, so the result never removes more than it claims. Null when no
 * overlap can be carved out. `extraCount` sums the backend's `exclusionExtraCount`: items a
 * best-effort (single-field) carve-out also removes that the overlapping case does not select.
 */
export function ownedExclusion(overlaps: InvestigationOverlap[]): {
  predicates: InvestigationRule;
  itemCount: number;
  caseCount: number;
  extraCount: number;
} | null {
  const byField = new Map<
    string,
    {
      exclude: boolean;
      values: Set<string>;
      count: number;
      cases: number;
      extra: number;
    }
  >();
  const conflicting = new Set<string>();
  overlaps.forEach((o) => {
    const exclusion = o.exclusion ?? [];
    exclusion.forEach((p) => {
      const field = p.field as string;
      const exclude = isExclusion(p);
      const values = exclude ? excludedValues(p) : predicateValues(p);
      const current = byField.get(field);
      if (!current) {
        byField.set(field, {
          exclude,
          values: new Set(values),
          count: 0,
          cases: 0,
          extra: 0,
        });
      } else if (current.exclude !== exclude) {
        conflicting.add(field);
      } else if (exclude) {
        values.forEach((v) => current.values.add(v));
      } else {
        current.values = new Set(values.filter((v) => current.values.has(v)));
      }
    });
    // A case carved out on several fields counts once, against its first field.
    const first = exclusion[0]?.field as string | undefined;
    const entry = first ? byField.get(first) : undefined;
    if (entry) {
      entry.count += o.overlappingCount ?? 0;
      entry.cases += 1;
      entry.extra += o.exclusionExtraCount ?? 0;
    }
  });
  const predicates: InvestigationRule = [];
  let itemCount = 0;
  let caseCount = 0;
  let extraCount = 0;
  byField.forEach((entry, field) => {
    if (conflicting.has(field) || !entry.values.size) return;
    const values = Array.from(entry.values).sort();
    if (values.length > MAX_RULE_VALUES) return;
    const predicate = entry.exclude
      ? ({
          field,
          operator: "not_in",
          values,
        } as unknown as InvestigationPredicate)
      : makePredicate(field as InvestigationPredicate["field"], values);
    if (!predicate) return;
    predicates.push(predicate);
    itemCount += entry.count;
    caseCount += entry.cases;
    extraCount += entry.extra;
  });
  return predicates.length
    ? { predicates, itemCount, caseCount, extraCount }
    : null;
}

/** `value` or `a, b, c` — the human form of a predicate's value side (excluded values for `not_in`). */
export const predicateValueText = (
  p: InvestigationPredicate,
  joiner = ", "
): string =>
  (isExclusion(p) ? excludedValues(p) : predicateValues(p)).join(joiner);

/** "is" / "is one of" / "is none of" — the human form of a predicate's operator. */
export const predicateOperatorText = (p: InvestigationPredicate): string =>
  isExclusion(p)
    ? "is none of"
    : predicateValues(p).length > 1
    ? "is one of"
    : "is";

/** Stable key for a predicate within a rule (fields are unique per rule). */
export const predicateKey = (p: InvestigationPredicate): string =>
  isExclusion(p)
    ? `${p.field}!=${excludedValues(p).join("|")}`
    : `${p.field}=${predicateValues(p).join("|")}`;

/** True when both predicates bind the same field to the same value set (and operator kind). */
export const samePredicate = (
  a: InvestigationPredicate,
  b: InvestigationPredicate
): boolean => a.field === b.field && predicateKey(a) === predicateKey(b);

export function seedValue(seed: RowSeed, field: string): string | undefined {
  const value = (seed as unknown as Record<string, string | undefined>)[field];
  return value === undefined || value === null || value === ""
    ? undefined
    : String(value);
}

/**
 * The seed item's value for a field as recorded, keeping "" (the item has no value) distinct
 * from undefined (the seed never carried the field). `seedValue` folds both into undefined.
 */
export function rawSeedValue(seed: RowSeed, field: string): string | undefined {
  const value = (seed as unknown as Record<string, string | undefined>)[field];
  return value === undefined || value === null ? undefined : String(value);
}

/**
 * Predicates of `rule` that the seed DLQ item does not satisfy — i.e. the reasons the item is no
 * longer in the population. Fields the seed never carried are not judged.
 */
export function predicatesExcludingSeed(
  rule: InvestigationRule,
  seed: RowSeed
): InvestigationPredicate[] {
  return rule.filter((p) => {
    const mine = rawSeedValue(seed, p.field);
    if (mine === undefined) return false;
    return !predicateAccepts(p, mine);
  });
}

/** Builder seed from a snapshot sample: every rule field, plus display-only transaction identity. */
export function sampleToSeed(sample: InvestigationSample): RowSeed {
  return {
    dlqPk: sample.dlqPk || "",
    eventsourcekey: sample.eventsourcekey ?? undefined,
    error_type: sample.errorType ?? undefined,
    error_message: sample.errorMessage ?? undefined,
    error_category: sample.errorCategory ?? undefined,
    gl_product_group: sample.glProductGroup ?? undefined,
    marketplace_id: sample.marketplaceId ?? undefined,
    usecase: sample.usecase ?? undefined,
    digital_order_source: sample.digitalOrderSource ?? undefined,
    is_emp_order: sample.isEmpOrder ?? undefined,
    destination: sample.destination ?? undefined,
    accounting_operation: sample.accountingOperation ?? undefined,
    payment_processor: sample.paymentProcessor ?? undefined,
    exception_fingerprint: sample.exceptionFingerprint ?? undefined,
    flare_error_types: sample.flareErrorTypes ?? undefined,
    flare_invalid_values: sample.flareInvalidValues ?? undefined,
    flare_missing_attributes: sample.flareMissingAttributes ?? undefined,
    flare_failed_schema_definitions:
      sample.flareFailedSchemaDefinitions ?? undefined,
    flare_attributes_not_covered: sample.flareAttributesNotCovered ?? undefined,
    flare_balance_scope: sample.flareBalanceScope ?? undefined,
    flare_balance_scope_value: sample.flareBalanceScopeValue ?? undefined,
    transaction_id: sample.transactionId ?? undefined,
    order_id: sample.orderId ?? undefined,
  };
}

/**
 * Builder seed from an existing investigation's rule (the "Split" flow): every predicate becomes
 * the seed value for its field. There is no DLQ item behind it, so `dlqPk` is empty.
 */
export function ruleToSeed(
  rule: InvestigationRule | null | undefined
): RowSeed {
  const seed: Record<string, string> = { dlqPk: "" };
  (rule || []).forEach((p) => {
    const [only, ...more] = predicateValues(p);
    // A multi-valued field has no single seed value; the builder carries the set in `overrides`.
    if (only !== undefined && !more.length) seed[p.field] = only;
  });
  return seed as unknown as RowSeed;
}

/** Per-field value sets a builder holds beyond the seed row: what the user picked or typed. */
export type RuleOverrides = Record<string, string[]>;

/** The values a builder field currently carries: its override when set, else the seed's value. */
export function fieldValues(
  seed: RowSeed,
  field: string,
  overrides: RuleOverrides = {}
): string[] {
  const override = overrides[field];
  if (override !== undefined) return override.filter((v) => v !== "");
  const fromSeed = seedValue(seed, field);
  return fromSeed ? [fromSeed] : [];
}

export function buildRule(
  seed: RowSeed,
  selected: Set<string>,
  overrides: RuleOverrides = {}
): InvestigationRule {
  return RULE_FIELDS.filter((f) => selected.has(f.field)).flatMap((f) => {
    const values = fieldValues(seed, f.field, overrides).map((v) =>
      f.boolean ? v.toLowerCase() : v
    );
    // A boolean is true or false, never both.
    const predicate = makePredicate(
      f.field,
      f.boolean ? values.slice(0, 1) : values
    );
    return predicate ? [predicate] : [];
  });
}

/** The rule the population builder starts with: every default-selected field the row has a value for. */
export function defaultRule(seed: RowSeed): InvestigationRule {
  return buildRule(
    seed,
    new Set(RULE_FIELDS.filter((f) => f.defaultSelected).map((f) => f.field))
  );
}

/**
 * How useful adding a field to the rule would be, judged from its value distribution over the
 * previewed population ("suggested splits").
 *
 * - `uniform`:   one value — pinning it changes nothing (but confirms the row is representative)
 * - `split`:     a handful of values with no single one dominating — the natural way to divide
 * - `outlier`:   one value covers almost everything; the rest is a small, distinct population
 * - `fragmented`: too many values to be a meaningful grouping
 */
export type SplitVerdict = "uniform" | "split" | "outlier" | "fragmented";

export const SPLIT_OUTLIER_SHARE = 0.9;
export const SPLIT_MAX_DISTINCT = 20;

export interface SplitSuggestion {
  field: string;
  label: string;
  boolean: boolean;
  verdict: SplitVerdict;
  distinctValues: number;
  totalCount: number;
  /** Top values with their share of the population, descending. */
  values: Array<{ value: string; count: number; share: number }>;
  otherCount: number;
  /** Rank for display: best splits first, uniform fields last. */
  score: number;
}

/** Shannon entropy normalised to [0, 1] over the reported values (+ the folded remainder). */
function evenness(counts: number[], total: number): number {
  const buckets = counts.filter((c) => c > 0);
  if (buckets.length < 2 || total <= 0) return 0;
  const h = buckets.reduce((acc, c) => {
    const p = c / total;
    return acc - p * Math.log(p);
  }, 0);
  return h / Math.log(buckets.length);
}

export function classifySplit(
  breakdown: InvestigationFieldBreakdown
): SplitSuggestion | null {
  const definition = RULE_FIELDS.find((f) => f.field === breakdown.field);
  if (!definition) return null;
  const total = breakdown.totalCount;
  const values = (breakdown.values || []).map((v) => ({
    value: v.value,
    count: v.count,
    share: total > 0 ? v.count / total : 0,
  }));
  const topShare = values[0]?.share ?? 0;
  let verdict: SplitVerdict;
  if (breakdown.distinctValues <= 1) verdict = "uniform";
  else if (breakdown.distinctValues > SPLIT_MAX_DISTINCT)
    verdict = "fragmented";
  else if (topShare >= SPLIT_OUTLIER_SHARE) verdict = "outlier";
  else verdict = "split";
  const spread = evenness(
    [...values.map((v) => v.count), breakdown.otherCount],
    total
  );
  const baseScore: Record<SplitVerdict, number> = {
    split: 3 + spread,
    outlier: 2 + (1 - topShare),
    fragmented: 1,
    uniform: 0,
  };
  const score = baseScore[verdict];
  return {
    field: breakdown.field,
    label: definition.label,
    boolean: !!definition.boolean,
    verdict,
    distinctValues: breakdown.distinctValues,
    totalCount: total,
    values,
    otherCount: breakdown.otherCount,
    score,
  };
}

/** Suggestions for every reported field, most useful first. */
export function suggestSplits(
  fieldBreakdown: InvestigationFieldBreakdown[] | undefined
): SplitSuggestion[] {
  return (fieldBreakdown || [])
    .map(classifySplit)
    .filter((s): s is SplitSuggestion => s !== null)
    .sort((a, b) => b.score - a.score);
}

/**
 * Stages where the row resolver previews the default population as soon as a row turns out to
 * be uninvestigated, so a one-click "Create and claim" is offered. Prod only: its snapshot is
 * ~2.6 GB, so a preview costs about a cent; Gamma's 751 GB unpartitioned view is ~$0.05–0.08 per
 * uncached preview and keeps the explicit builder step. Athena result reuse (24 h, per snapshot)
 * and the in-tab recent-preview cache make repeats free either way.
 */
export const AUTO_PREVIEW_STAGES: ReadonlySet<string> = new Set([Stage.Prod]);

export function autoPreviewEnabled(stage: string): boolean {
  return AUTO_PREVIEW_STAGES.has(stage);
}

/** Mirrors MAX_TITLE_LENGTH on the server; a suggested title must never be rejected for length. */
export const MAX_SUGGESTED_TITLE_LENGTH = 200;

/**
 * Fields the suggested title spells out as `label=value` when they are in the rule. The headline
 * fields (category, type, queue) have fixed slots; the templated error message is left out because
 * it is a sentence, not a token.
 */
const TITLE_EXTRA_LABELS: Record<string, string> = {
  gl_product_group: "GL",
  marketplace_id: "marketplace",
  usecase: "use case",
  digital_order_source: "source",
  is_emp_order: "EMP",
  destination: "destination",
  accounting_operation: "operation",
  payment_processor: "processor",
};

/**
 * Short `label=value` tokens for the rule's narrowing predicates, in RULE_FIELDS order. Exported so
 * a split's title can name only the predicates it adds beyond its parent.
 */
export function titleExtras(
  rule: InvestigationRule,
  exclude: ReadonlySet<string> = new Set()
): string[] {
  const byField = new Map(
    rule
      .filter((p) => !isExclusion(p))
      .map((p) => [p.field as string, predicateValueText(p, "|").trim()])
  );
  return RULE_FIELDS.flatMap((f) => {
    const label = TITLE_EXTRA_LABELS[f.field];
    const value = byField.get(f.field);
    return label && value && !exclude.has(f.field) ? [`${label}=${value}`] : [];
  });
}

/** Drops extras from the end, then hard-truncates, until the title fits the server limit. */
function fitTitle(
  compose: (extras: string[]) => string,
  extras: string[]
): string {
  const remaining = [...extras];
  let title = compose(remaining);
  while (title.length > MAX_SUGGESTED_TITLE_LENGTH && remaining.length) {
    remaining.pop();
    title = compose(remaining);
  }
  return title.length > MAX_SUGGESTED_TITLE_LENGTH
    ? title.slice(0, MAX_SUGGESTED_TITLE_LENGTH)
    : title;
}

/** Longest templated message a suggested title quotes before cutting it with an ellipsis. */
const MAX_TITLE_MESSAGE_LENGTH = 60;

/** Last dotted segment of a Java name: `a.b.Foo.getBar()` → `getBar()`. */
const lastSegment = (name: string) =>
  name.replace(/[<>]/g, "").split(".").pop() || name;

/**
 * Known DLQ message shapes and the short form a title uses for each, tried in order. Each
 * pattern runs against the whitespace-collapsed message; the first match wins.
 */
const MESSAGE_SHORTENERS: ReadonlyArray<
  [RegExp, (m: RegExpMatchArray) => string]
> = [
  // Java helpful NPE: `Cannot invoke "x.Amount.value()" because "amount" is null`.
  [
    /because (?:the return value of )?"([^"]+)" is null/,
    (m) => `${lastSegment(m[1])} is null`,
  ],
  // Lombok: `transactionDate is marked non-null but is null`.
  [/^(\S+) is marked non-null but is null/, (m) => `${m[1]} is null`],
  // FLARE: `NZD is not a valid value for attribute [functional_amount,currency_code].`
  [
    /^(\S+) is not a valid value for attribute \[?([^\]]*?)\]?\.?$/,
    (m) => `${m[1]} invalid for ${m[2].split(",").pop()?.trim() || m[2]}`,
  ],
  // FLARE: `The credit and debit values for all the records under set_of_books: 1 should net…`
  [
    /credit and debit values for all the records under (\w+): ?([\w-]*)/,
    (m) => `unbalanced ${m[1]}${m[2] ? ` ${m[2]}` : ""}`,
  ],
  // FLARE: `Failed to derive attributes: [a, b` (brackets often cut off by the template).
  [
    /^Failed to derive attributes: \[?([^\]]+)\]?/,
    (m) => `cannot derive ${m[1].trim()}`,
  ],
  // FLARE: "Missing required attribute(s). `origin: input` attributes are missing …".
  [
    /^Missing required attribute\(s\)\. `origin: (\w+)`/,
    (m) => `missing ${m[1]} attributes`,
  ],
  // RACE wrapping FLARE: `FlareException ErrorList : [{error_type:missing_required_…`.
  [/^FlareException .*?error_type:(\w+)/, (m) => `FLARE ${m[1]}`],
  // PSAA/PayStation: `ErrorResponse(summary=Summary(… detailedStatusCode=DependencyFailure, …`.
  [
    /^ErrorResponse\(summary=.*PSAA/,
    (m) => {
      const status = m[0].match(/detailedStatusCode=(\w+)/);
      return status ? `PSAA ${status[1]}` : "PSAA call failed";
    },
  ],
  // DynamoDB: `Transaction cancelled, … [TransactionConflict] (Service: DynamoDb, …`.
  [
    /^Transaction cancelled.*?\[(\w+)\]/,
    (m) => `Transaction cancelled: ${m[1]}`,
  ],
  // GUITAR: `AuditTrailIndex: <*> … Source Exception Type: x.Y Failed to fetch … - retry …`.
  [
    /Source Exception Type: \S+ (.+?)(?: - |\. |$)/,
    (m) => m[1].replace(/\.$/, ""),
  ],
];

/**
 * A templated error message shortened for a title. Known shapes (Java NPEs, FLARE validation
 * errors, PSAA error responses, …) collapse to the part that names the problem, e.g.
 * `Cannot invoke "a.b.Amount.value()" because "amount" is null` → "amount is null". Anything
 * else loses a leading exception class, AWS SDK request details, trailing `<*>` placeholders and
 * everything after its first sentence, then is capped with an ellipsis.
 */
export function shortErrorMessage(message: string): string {
  const text = message.replace(/\s+/g, " ").trim();
  const known = MESSAGE_SHORTENERS.map(([pattern, shorten]) => {
    const m = text.match(pattern);
    return m ? shorten(m) : undefined;
  }).find(Boolean);
  const short = (
    known ??
    text
      .replace(/^(?:[a-z_$][\w$]*\.)+[A-Z][\w$]*: /, "") // "java.lang.FooException: "
      .replace(/ \(Service: .*$/, "") // AWS SDK "(Service: …, Request ID: …)"
      .replace(/^(.+?[.!?])(?: .*)?$/, "$1") // first sentence
      .replace(/(?:\s*<\*>)+\s*[.:]?$/, "") // trailing placeholders
      .replace(/\.$/, "")
  ).trim();
  return short.length > MAX_TITLE_MESSAGE_LENGTH
    ? `${short.slice(0, MAX_TITLE_MESSAGE_LENGTH - 1).trimEnd()}…`
    : short;
}

/**
 * Suggested title for the population a rule selects:
 * "<error category>: <error type> (<label>=<value>, …) on <queue>". When the rule pins exactly one
 * templated error message, its short form (see `shortErrorMessage`) replaces the error type.
 * The category leads because it
 * is the one human-assigned attribute on a DLQ item — the error type and queue are whatever the
 * pipeline emitted. Every slot reflects the rule, not the seed row, so unticking a field drops it
 * from the title and narrowing on marketplace, source, etc. shows up as `label=value` tokens —
 * otherwise two differently scoped investigations would read identically. Ends in " on <queue>"
 * (the table strips that suffix for display, so it must stay last).
 */
export function titleForRule(rule: InvestigationRule): string {
  const value = (field: string) => {
    const p = rule.find((q) => q.field === field && !isExclusion(q));
    return p ? predicateValueText(p, "|").trim() || undefined : undefined;
  };
  const category = value("error_category");
  // One pinned message says more than the error type it belongs to, so it takes the type's slot.
  const messagePredicate = rule.find(
    (q) => q.field === "error_message" && !isExclusion(q)
  );
  const messageValues = messagePredicate
    ? predicateValues(messagePredicate)
    : [];
  const message =
    messageValues.length === 1 ? shortErrorMessage(messageValues[0]) : "";
  const errorType = message || value("error_type") || "All errors";
  const queue = value("eventsourcekey") || "any queue";
  return fitTitle((extras) => {
    const scope = extras.length ? ` (${extras.join(", ")})` : "";
    const headline = `${errorType}${scope} on ${queue}`;
    return category ? `${category}: ${headline}` : headline;
  }, titleExtras(rule));
}

/**
 * Suggested title for a population TrackStars named: its proposed error_category verbatim
 * (e.g. "document_builder_missing_payment_contract_id_tag_error" — the underscored name is the
 * identifier people search for, so it is never reformatted), with the queue and scope tokens the
 * rule pins so two scoped cases of one category still differ. Stays within the server title limit
 * like every other suggestion.
 */
export function trackStarsTitle(
  category: string,
  rule: InvestigationRule
): string {
  const headline = category.trim();
  const queuePredicate = rule.find((p) => p.field === "eventsourcekey");
  const queue = queuePredicate
    ? predicateValueText(queuePredicate, "|").trim()
    : undefined;
  return fitTitle((extras) => {
    const scope = extras.length ? ` (${extras.join(", ")})` : "";
    return `${headline}${scope}${queue ? ` on ${queue}` : ""}`;
  }, titleExtras(rule));
}

/**
 * Suggested title for a child split from `parentTitle`: the parent's title plus the predicates
 * the child adds, so two splits of one parent do not end up with the same name.
 */
export function splitTitle(
  parentTitle: string,
  rule: InvestigationRule,
  lockedFields: ReadonlySet<string>
): string {
  return fitTitle((extras) => {
    const scope = extras.length ? ` (${extras.join(", ")})` : "";
    return `${parentTitle} – split${scope}`;
  }, titleExtras(rule, lockedFields));
}

/** Suggested title for the default rule a row starts the builder with (one-click create paths). */
export function defaultInvestigationTitle(seed: RowSeed): string {
  return titleForRule(defaultRule(seed));
}

/** Snapshot the population service queries (mirrors backend DLQ_DATABASE_NAME / DLQ_TABLE_NAME). */
export const DLQ_SNAPSHOT_TABLE = '"dlq_data"."latest_dlq_event_data"';

/** Athena workgroup the backend runs population queries in (mirrors ENVIRONMENT_CONFIG). */
export const athenaWorkgroup = (stage: string): string =>
  `${stage.toLowerCase()}-ops-athena-workgroup`;

const sqlLiteral = (value: string): string => `'${value.replace(/'/g, "''")}'`;

/**
 * Renders a population rule as the Athena SQL the backend uses to freeze the population (same
 * predicate semantics as `rule_to_sql_where`: `field = 'value'` or `field IN (...)` joined by AND,
 * booleans unquoted).
 * `SELECT *` so a human can inspect every column; the backend selects an explicit column list.
 */
export function ruleToAthenaQuery(
  rule: InvestigationRule | InvestigationRule[] | null | undefined,
  stage?: string
): string {
  // One rule (list of predicates) or several OR branches (list of lists).
  const branches: InvestigationRule[] =
    rule && rule.length && Array.isArray(rule[0])
      ? (rule as InvestigationRule[]).filter((b) => b.length)
      : rule && rule.length
      ? [rule as InvestigationRule]
      : [];
  const isBoolean = (field: string) =>
    RULE_FIELDS.some((f) => f.field === field && f.boolean);
  const clause = (p: InvestigationPredicate): string => {
    if (isExclusion(p)) {
      // NULL / empty is how the snapshot spells "uncategorised"; NOT IN alone would drop it.
      const listed = [...excludedValues(p)].sort().map(sqlLiteral).join(", ");
      return `(${p.field} IS NULL OR ${p.field} NOT IN (${listed}))`;
    }
    const values = [...predicateValues(p)].sort();
    if (isBoolean(p.field)) {
      return `${p.field} = ${
        values[0]?.toLowerCase() === "true" ? "true" : "false"
      }`;
    }
    if (values.length === 1) return `${p.field} = ${sqlLiteral(values[0])}`;
    return `${p.field} IN (${values.map(sqlLiteral).join(", ")})`;
  };
  const branchWhere = (predicates: InvestigationRule) =>
    predicates.map(clause).join("\n  AND ");
  const where = !branches.length
    ? "1 = 1"
    : branches.length === 1
    ? branchWhere(branches[0])
    : branches.map((b) => `(${branchWhere(b)})`).join("\n  OR ");
  const header = stage
    ? `-- Athena workgroup: ${athenaWorkgroup(stage)}\n`
    : "";
  return `${header}SELECT *\nFROM ${DLQ_SNAPSHOT_TABLE}\nWHERE ${where}`;
}

export const STATUS_LABELS: Record<string, string> = {
  INVESTIGATING: "Investigating",
  BLOCKED: "Blocked",
  FIX_IN_PROGRESS: "Fix in progress",
  READY_TO_REDRIVE: "Ready to redrive",
  EXECUTING_REDRIVE: "Executing redrive",
  MONITORING_REDRIVE: "Monitoring redrive",
  RESOLVED: "Resolved",
  WITHDRAWN: "Cancelled",
};

/**
 * The two terminal states. Both release the population; only RESOLVED claims an outcome. A
 * withdrawn case was let go (wrong scope, mistake, consolidated into a wider one) — its items are
 * simply back in the pool, so it is never counted or listed as "resolved".
 */
export const TERMINAL_STATUSES = ["RESOLVED", "WITHDRAWN"];
export const isTerminal = (status?: string | null): boolean =>
  !!status && TERMINAL_STATUSES.includes(status);

/**
 * The status to show for a case. A merged source and a case auto-closed as a duplicate (its claim
 * won no items) are RESOLVED on the wire (the backend never lets them reopen) but were closed by
 * Lusca without an outcome of their own, so they read and list as Cancelled, never as Resolved.
 * A person resolving a case as Duplicate sets no duplicateOfInvestigationId and stays Resolved.
 */
export const shownStatus = (inv: {
  status?: string | null;
  mergedIntoInvestigationId?: string | null;
  duplicateOfInvestigationId?: string | null;
  resolution?: { reason?: string | null } | null;
}): string =>
  inv.status === "RESOLVED" &&
  (inv.mergedIntoInvestigationId ||
    inv.duplicateOfInvestigationId ||
    inv.resolution?.reason === "MERGED")
    ? "WITHDRAWN"
    : inv.status ?? "";

export const STATUS_INDICATOR: Record<string, StatusIndicatorProps.Type> = {
  INVESTIGATING: "in-progress",
  // Red: a blocked case needs attention, it isn't merely paused.
  BLOCKED: "error",
  FIX_IN_PROGRESS: "pending",
  READY_TO_REDRIVE: "info",
  EXECUTING_REDRIVE: "loading",
  MONITORING_REDRIVE: "pending",
  RESOLVED: "success",
  WITHDRAWN: "stopped",
};

/**
 * Human-selectable transitions; EXECUTING/MONITORING are system-owned. From Monitoring redrive
 * a human may park the case (Blocked) or act on what the redrive revealed (Investigating /
 * Fix in progress / Ready to redrive again) — but not withdraw: the redrive has acted on the
 * population, so the case can no longer claim it should never have been opened. Use
 * `humanTransitions` rather than indexing this table directly: a case blocked *during*
 * monitoring unblocks back to Monitoring redrive.
 */
export const HUMAN_TRANSITIONS: Record<string, string[]> = {
  INVESTIGATING: [
    "BLOCKED",
    "FIX_IN_PROGRESS",
    "READY_TO_REDRIVE",
    "RESOLVED",
    "WITHDRAWN",
  ],
  BLOCKED: [
    "INVESTIGATING",
    "FIX_IN_PROGRESS",
    "READY_TO_REDRIVE",
    "RESOLVED",
    "WITHDRAWN",
  ],
  FIX_IN_PROGRESS: [
    "INVESTIGATING",
    "BLOCKED",
    "READY_TO_REDRIVE",
    "RESOLVED",
    "WITHDRAWN",
  ],
  READY_TO_REDRIVE: [
    "INVESTIGATING",
    "BLOCKED",
    "FIX_IN_PROGRESS",
    "RESOLVED",
    "WITHDRAWN",
  ],
  EXECUTING_REDRIVE: [],
  MONITORING_REDRIVE: [
    "INVESTIGATING",
    "BLOCKED",
    "FIX_IN_PROGRESS",
    "READY_TO_REDRIVE",
    "RESOLVED",
  ],
  RESOLVED: ["INVESTIGATING"],
  WITHDRAWN: ["INVESTIGATING"],
};

/**
 * Transitions a human may request from `status`. Mirrors the backend: the only way into a
 * system-owned status is unblocking a case that was blocked while Lusca was monitoring its
 * redrive, which resumes that monitoring.
 */
export const humanTransitions = (
  status: string,
  blockedFrom?: string | null
): string[] => {
  const base = HUMAN_TRANSITIONS[status] || [];
  if (status === "BLOCKED" && blockedFrom === "MONITORING_REDRIVE") {
    return ["MONITORING_REDRIVE", ...base];
  }
  return base;
};

/**
 * What the case's redrive history means for someone reading the Status card. Only relevant once
 * an attempt exists and the case is back on the investigative side of the path — the redrive
 * step itself already explains a case that is in Executing/Monitoring, and a resolved case has
 * its resolution. `outcome` drives the tone: "failed" (state machine failed), "returned" (Lusca
 * saw items come back), "abandoned" (completed cleanly but the owner moved back anyway).
 */
export interface RedriveHistory {
  attempts: number;
  attemptId?: string;
  kind: "REDRIVE" | "DELETE";
  startedAt?: string | null;
  outcome: "failed" | "returned" | "abandoned";
  returnedCount: number;
}

export const redriveHistory = (
  investigation: Pick<
    Investigation,
    "status" | "redriveAttemptCount" | "latestRedrive" | "returnedCount"
  >
): RedriveHistory | null => {
  const attempts = investigation.redriveAttemptCount ?? 0;
  const investigative = [
    "INVESTIGATING",
    "BLOCKED",
    "FIX_IN_PROGRESS",
    "READY_TO_REDRIVE",
  ];
  if (attempts === 0 || !investigative.includes(investigation.status)) {
    return null;
  }
  const latest = investigation.latestRedrive;
  const returnedCount = investigation.returnedCount ?? 0;
  let outcome: RedriveHistory["outcome"] = "abandoned";
  if (latest?.status === "FAILED") outcome = "failed";
  else if (latest?.hasReturns || returnedCount > 0) outcome = "returned";
  return {
    attempts,
    attemptId: latest?.attemptId,
    kind: latest?.kind === "DELETE" ? "DELETE" : "REDRIVE",
    startedAt: latest?.startedAt,
    outcome,
    returnedCount,
  };
};

export const RESOLUTION_REASONS = [
  { value: "FIXED_AND_REDRIVEN", label: "Fixed and redriven" },
  { value: "FIXED_WITHOUT_REDRIVE", label: "Fixed without redrive" },
  { value: "DELETED_WITHOUT_REDRIVE", label: "Deleted without redrive" },
  { value: "NO_ACTION_REQUIRED", label: "No action required" },
  { value: "DUPLICATE", label: "Duplicate" },
  { value: "SUPERSEDED", label: "Superseded" },
];

/**
 * The reason Lusca records on the resolution block of a WITHDRAWN case. Withdrawing is not a
 * resolution — it is its own terminal status — but history rows carry one field to read.
 */
export const RESOLUTION_WITHDRAWN = "WITHDRAWN";

/**
 * The reason the merge action records on a source absorbed into another investigation. Never
 * offered in the resolve dialog; such a case cannot be reopened (split the survivor instead).
 */
export const RESOLUTION_MERGED = "MERGED";

/** Human label for any resolution reason, including the two system-recorded ones. */
export function resolutionReasonLabel(reason?: string | null): string {
  if (!reason) return "—";
  if (reason === RESOLUTION_MERGED) return "Merged";
  if (reason === RESOLUTION_WITHDRAWN) return "Cancelled";
  return (
    RESOLUTION_REASONS.find((r) => r.value === reason)?.label ||
    reason.toLowerCase().replace(/_/g, " ")
  );
}

// ---- Rule branches --------------------------------------------------------------------------------
//
// A merged investigation carries several rules (OR branches); an unmerged one carries one. Every
// component reads the branches through `caseRules` so the legacy `rule` field keeps working.

/** The rule branches of an investigation: `rules` when present, else the single `rule`. */
export function caseRules(
  investigation: Pick<Investigation, "rule" | "rules"> | null | undefined
): InvestigationRule[] {
  if (!investigation) return [];
  const branches = (investigation.rules ?? []).filter((b) => b && b.length);
  if (branches.length) return branches as InvestigationRule[];
  return investigation.rule?.length ? [investigation.rule] : [];
}

/**
 * Predicates every branch shares (same field, same value): what the whole population has in
 * common, which is what the Items table hides as "already known".
 */
export function commonRulePredicates(
  investigation: Pick<Investigation, "rule" | "rules"> | null | undefined
): InvestigationRule {
  const branches = caseRules(investigation);
  if (!branches.length) return [];
  return branches[0].filter((p) =>
    branches.every((b) => b.some((q) => samePredicate(p, q)))
  );
}

/** Value of a field when every branch pins it to the same single value; "" otherwise. */
export function commonRuleValue(
  branches: InvestigationRule[],
  field: string
): string {
  if (!branches.length) return "";
  const values = branches.map((b) => {
    const p = b.find((q) => (q.field as string) === field);
    const set = p ? predicateValues(p) : [];
    return set.length === 1 ? set[0] : "";
  });
  return values.every((v) => v && v === values[0]) ? values[0] : "";
}

// ---- Related open cases (R13) ----------------------------------------------------------------------

/** Frontend port of the backend's single-branch `rules_overlap`. */
export type RuleRelation = "rule_subset" | "rule_superset" | "rule_overlap";

type ValueSets = Map<string, Set<string>>;

// `not_in` predicates bind no finite set and are left out (the hints this feeds are advisory;
// the backend's rules_overlap is exact).
const ruleToMap = (rule: InvestigationRule): ValueSets =>
  new Map(
    rule
      .filter((p) => !isExclusion(p))
      .map((p) => [p.field as string, new Set(predicateValues(p))])
  );

/** Every item `inner` selects, `outer` selects too (every field outer pins, inner pins to a subset). */
const contains = (inner: ValueSets, outer: ValueSets): boolean =>
  Array.from(outer).every(([field, values]) => {
    const mine = inner.get(field);
    return !!mine && Array.from(mine).every((v) => values.has(v));
  });

/** True when some shared field has disjoint value sets: no item can satisfy both rules. */
const disjoint = (ma: ValueSets, mb: ValueSets): boolean =>
  Array.from(ma).some(([field, va]) => {
    const vb = mb.get(field);
    return !!vb && !Array.from(va).some((v) => vb.has(v));
  });

/** True when every item `inner` selects, `outer` selects too (inner is within outer, or equal). */
export const ruleWithin = (
  inner: InvestigationRule,
  outer: InvestigationRule
): boolean => contains(ruleToMap(inner), ruleToMap(outer));

/**
 * The predicates `pattern` would add to (or tighten in) `rule` so the population is within the
 * pattern's: every pattern field the rule does not already pin to a subset of the pattern's values.
 */
export const predicatesToNarrow = (
  rule: InvestigationRule,
  pattern: InvestigationRule
): InvestigationRule => {
  const mine = ruleToMap(rule);
  return pattern.filter((p) => {
    const values = mine.get(p.field as string);
    const theirs = new Set(predicateValues(p));
    return !values || !Array.from(values).every((v) => theirs.has(v));
  });
};

export function rulesOverlap(
  a: InvestigationRule,
  b: InvestigationRule
): RuleRelation | null {
  const ma = ruleToMap(a);
  const mb = ruleToMap(b);
  if (disjoint(ma, mb)) return null;
  const aInB = contains(ma, mb);
  const bInA = contains(mb, ma);
  if (aInB && !bInA) return "rule_subset";
  if (bInA && !aInB) return "rule_superset";
  return "rule_overlap";
}

/** True when the draft rule pins error_category to the transient "uncategorized" bucket. */
export const pinsUncategorized = (rule: InvestigationRule): boolean =>
  rule.some(
    (p) =>
      (p.field as string) === "error_category" &&
      !isExclusion(p) &&
      predicateAccepts(p, UNCATEGORIZED)
  );

/** The draft rule without the `error_category = uncategorized` predicate. */
export const withoutUncategorized = (
  rule: InvestigationRule
): InvestigationRule =>
  rule.filter(
    (p) =>
      !(
        (p.field as string) === "error_category" &&
        !isExclusion(p) &&
        predicateAccepts(p, UNCATEGORIZED)
      )
  );

export interface MatchingPattern {
  suggestion: InvestigationSuggestion;
  /** How the draft relates to the pattern's derived rule. */
  relation: RuleRelation;
}

/**
 * True when the previewed population's breakdown proves `rule` selects none of its items: some
 * field `rule` pins has a complete breakdown (no values folded into `otherCount`) and none of the
 * pinned values occur in it. Rules that merely fail to contradict the draft on paper (the draft
 * pins error_category, the pattern error_type) are dropped this way. Never hides a real overlap:
 * an incomplete breakdown, or a field the draft pins itself (not broken down), proves nothing.
 */
export function ruleMissesPopulation(
  rule: InvestigationRule,
  breakdown?: InvestigationFieldBreakdown[]
): boolean {
  if (!breakdown?.length) return false;
  return rule.some((p) => {
    if (isExclusion(p)) return false;
    const b = breakdown.find((x) => x.field === (p.field as string));
    if (!b || b.otherCount > 0) return false;
    const present = new Set(b.values.map((v) => (v.value ?? "").toLowerCase()));
    return !predicateValues(p).some((v) => present.has(v.toLowerCase()));
  });
}

/** One of a pattern's text conditions on a field (the API's InvestigationSuggestionConstraint). */
export interface TextConstraint {
  field?: string;
  contains?: string[];
  excludes?: string[];
  notIn?: string[];
}

/** True when the constraint says anything at all about its field. */
export const constraintHasTerms = (c: TextConstraint): boolean =>
  !!c.field &&
  !!(
    (c.contains ?? []).some(Boolean) ||
    (c.excludes ?? []).some(Boolean) ||
    (c.notIn ?? []).length
  );

/**
 * Whether one value of the constraint's field satisfies it: carries every `contains` fragment
 * (case-insensitive, as the backend does), none of the `excludes` fragments (as written) and is
 * none of `notIn`. Mirrors the backend's `text_satisfies`; every relaxation keeps a value.
 */
export const constraintAccepts = (
  value: string | null | undefined,
  c: TextConstraint
): boolean => {
  const text = value ?? "";
  if ((c.notIn ?? []).includes(text)) return false;
  const required = (c.contains ?? []).filter(Boolean);
  const lowered = text.toLowerCase();
  if (
    required.length &&
    !(text && required.every((f) => lowered.includes(f.toLowerCase())))
  )
    return false;
  return !(c.excludes ?? []).some((f) => f && text.includes(f));
};

/**
 * True when a pattern's text constraints ("message contains X", "does not contain Y") rule out
 * every item of the draft: the draft pins that field to values none of which satisfy them, or the
 * preview's complete breakdown of the field has no such value. Without this a pattern sharing only
 * the error type ("IllegalArgumentException") would match any draft.
 */
export function constraintsContradict(
  rule: InvestigationRule,
  suggestion: InvestigationSuggestion,
  breakdown?: InvestigationFieldBreakdown[]
): boolean {
  return (suggestion.constraints ?? []).some((c) => {
    if (!constraintHasTerms(c)) return false;
    const pinned = rule.find(
      (p) => (p.field as string) === c.field && !isExclusion(p)
    );
    if (pinned)
      return !predicateValues(pinned).some((v) => constraintAccepts(v, c));
    const b = breakdown?.find((x) => x.field === c.field);
    if (!b || b.otherCount > 0) return false;
    return !b.values.some((v) => constraintAccepts(v.value, c));
  });
}

/**
 * TrackStars patterns whose derived rule can share items with the draft (R14.2). Patterns with
 * no usable derived rule never match.
 */
export function matchingPatterns(
  rule: InvestigationRule,
  suggestions: InvestigationSuggestion[] | undefined,
  breakdown?: InvestigationFieldBreakdown[]
): MatchingPattern[] {
  if (!rule.length) return [];
  return (suggestions ?? []).flatMap((suggestion) => {
    const derived = (suggestion.derivedRule ?? []) as InvestigationRule;
    if (!derived.length) return [];
    const relation = rulesOverlap(rule, derived);
    if (!relation || ruleMissesPopulation(derived, breakdown)) return [];
    if (constraintsContradict(rule, suggestion, breakdown)) return [];
    return [{ suggestion, relation }];
  });
}

/** The value a rule binds `error_category` to, or undefined when it is not part of the rule. */
export const ruleCategory = (rule: InvestigationRule): string | undefined => {
  const p = rule.find(
    (q) => (q.field as string) === "error_category" && !isExclusion(q)
  );
  return p ? predicateValueText(p, "|") : undefined;
};

/**
 * The grouping key that says "these two cases are about the same kind of failure" even when
 * their populations are disjoint (different marketplace, GL group…). Categorised: the
 * `error_category`. Uncategorised (or no category in the rule): the error type + message, which
 * is what the categoriser will eventually key on too.
 */
export function ruleCategoryKey(rule: InvestigationRule): string | null {
  if (!rule.length) return null;
  const category = ruleCategory(rule);
  if (category && category !== "uncategorized") return `category:${category}`;
  const typePredicate = rule.find((p) => (p.field as string) === "error_type");
  const messagePredicate = rule.find(
    (p) => (p.field as string) === "error_message"
  );
  const type = typePredicate
    ? predicateValueText(typePredicate, "|")
    : undefined;
  const message = messagePredicate
    ? predicateValueText(messagePredicate, "|")
    : undefined;
  if (!type && !message) return null;
  return `error:${type ?? ""}|${message ?? ""}`;
}

/** Short label for a category key: the category name, or "error type" for the fallback. */
export function categoryKeyLabel(key: string): string {
  if (key.startsWith("category:")) return key.slice("category:".length);
  const [type] = key.slice("error:".length).split("|");
  return type || "same error";
}

export interface RelatedOpenCase {
  investigation: Investigation;
  /** Predicates of the draft rule the other case does not pin, and vice versa. */
  relation: "same" | "narrower" | "wider" | "sibling";
}

/**
 * Open investigations that share the draft rule's category key (R13.1). Works on any rule
 * branch of the other case; the relation says whether the other case is the same population,
 * a subset, a superset or a sibling (same category, disjoint or overlapping on other fields).
 */
export function relatedOpenCases(
  rule: InvestigationRule,
  active: Investigation[],
  excludeId?: string
): RelatedOpenCase[] {
  const key = ruleCategoryKey(rule);
  if (!key) return [];
  const draft = ruleToMap(rule);
  return active
    .filter((inv) => inv.id !== excludeId && inv.status !== "RESOLVED")
    .flatMap((inv) => {
      const branches = caseRules(inv);
      if (!branches.some((b) => ruleCategoryKey(b) === key)) return [];
      // Relation against the branch in the same category (the first one that matches).
      const branch = branches.find((b) => ruleCategoryKey(b) === key) ?? [];
      const other = ruleToMap(branch);
      let relation: RelatedOpenCase["relation"] = "sibling";
      if (!disjoint(draft, other)) {
        const draftInOther = contains(draft, other);
        const otherInDraft = contains(other, draft);
        if (draftInOther && otherInDraft) relation = "same";
        else if (draftInOther) relation = "narrower";
        else if (otherInDraft) relation = "wider";
      }
      return [{ investigation: inv, relation }];
    });
}

/**
 * Owner-private notification types (mirrors the backend `add_notification` call sites). The
 * badge is short so it fits beside the notification text.
 */
export const NOTIFICATION_LABELS: Record<string, string> = {
  COMMENT: "Comment",
  MENTION: "Mentioned you",
  CONTRIBUTION: "Contribution",
  TAKEOVER: "Ownership taken",
  TRANSFER: "Transferred to you",
  REDRIVE_COMPLETED: "Redrive completed",
  REDRIVE_FAILED: "Redrive failed",
  REDRIVE_RETURN: "Items returned to DLQ",
  REDRIVE_INCOMPLETE: "Items not redriven",
  MONITORING_CLEAR: "Ready to resolve",
  TRACKSTARS_WIDEN: "TrackStars found more",
  RECOUNTED: "Items recounted",
  ITEMS_TAKEN: "Items taken by a narrower case",
  RULE_OVERLAP: "Rule overlap",
  STALE_REMINDER: "No recent activity",
  FOLLOWED: "Following",
  DOCUMENT_REDRIVE_COMPLETED: "Order redrive completed",
  DOCUMENT_REDRIVE_FAILED: "Order redrive failed",
  DELETION_EXECUTED: "Deletion executed",
  DELETION_FAILED: "Deletion failed",
  DELETION_VERIFIED: "Deletion verified",
  DELETION_INCOMPLETE: "Deleted items still in DLQ",
  MERGED: "Merged into another case",
  DUPLICATE: "Duplicated an existing case",
};

export const notificationLabel = (type?: string | null): string =>
  (type && NOTIFICATION_LABELS[type]) ||
  (type ? type.toLowerCase().replace(/_/g, " ") : "Update");

/** Newest first; entries without a timestamp keep their stored order at the end. */
export function sortNotificationsNewestFirst<
  T extends { createdAt?: string | null }
>(notifications: T[]): T[] {
  const time = (n: T) => {
    const ms = n.createdAt ? new Date(n.createdAt).getTime() : NaN;
    return Number.isNaN(ms) ? -Infinity : ms;
  };
  return [...notifications].sort((a, b) => time(b) - time(a));
}

/**
 * Notification types that only report good news. They stay in the bell but never make a case
 * "need your attention": nothing is asked of the owner. Any other (or new) type counts.
 */
const INFORMATIONAL_NOTIFICATION_TYPES = new Set([
  "REDRIVE_COMPLETED",
  "FOLLOWED",
  "DELETION_EXECUTED",
  "DELETION_VERIFIED",
  "MERGED",
  "DUPLICATE",
]);

/** Unread, actionable inbox entries keyed by investigation id. */
export type AttentionInbox = ReadonlyMap<string, InvestigationInboxItem[]>;

/**
 * Index the viewer's inbox (the bell's feed) for the attention flag, so the two always agree:
 * an entry read in the bell no longer flags its case. Read and informational entries are
 * dropped.
 */
export function attentionInbox(
  items: InvestigationInboxItem[] | undefined | null
): AttentionInbox {
  const byCase = new Map<string, InvestigationInboxItem[]>();
  (items || [])
    .filter(
      (item) =>
        !item.readAt &&
        !(item.type && INFORMATIONAL_NOTIFICATION_TYPES.has(item.type))
    )
    .forEach((item) =>
      byCase.set(item.investigationId, [
        ...(byCase.get(item.investigationId) || []),
        item,
      ])
    );
  return byCase;
}

/**
 * Whether an investigation the viewer owns has something to look at: an unread, actionable
 * inbox entry or no human activity for the stale period.
 */
export function needsAttention(
  investigation: Investigation,
  currentUser: string | null,
  inbox?: AttentionInbox
): boolean {
  if (!currentUser || investigation.owner !== currentUser) return false;
  if (isTerminal(investigation.status)) return false;
  return !!inbox?.get(investigation.id)?.length || !!investigation.isStale;
}

/**
 * Owned investigations that need the viewer's attention, most urgent first: those with unread
 * notifications (newest notification first), then stale ones without notifications.
 */
export function attentionItems(
  items: Investigation[],
  currentUser: string | null,
  inbox?: AttentionInbox
): Investigation[] {
  const unread = (inv: Investigation) => inbox?.get(inv.id) || [];
  const latest = (inv: Investigation) => {
    const newest = sortNotificationsNewestFirst(unread(inv))[0];
    const ms = newest?.createdAt ? new Date(newest.createdAt).getTime() : NaN;
    return Number.isNaN(ms) ? -Infinity : ms;
  };
  return items
    .filter((inv) => needsAttention(inv, currentUser, inbox))
    .sort((a, b) => {
      const aCount = unread(a).length;
      const bCount = unread(b).length;
      if (!!aCount !== !!bCount) return aCount ? -1 : 1;
      return latest(b) - latest(a);
    });
}

/**
 * Impact (USD) at or above this is emphasised (bold red) in the investigation
 * and uninvestigated-groups tables. Shared so the two tables cannot drift.
 */
export const HIGH_IMPACT_USD = 250000;

export function formatTimestamp(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

/** Snapshot `job_run` (YYYYmmddHHMMSS, UTC) as a Date, or null when it does not parse. */
export function snapshotTimestampToDate(jobRun?: string | null): Date | null {
  if (!jobRun || jobRun.length < 12) return null;
  const date = new Date(
    `${jobRun.slice(0, 4)}-${jobRun.slice(4, 6)}-${jobRun.slice(
      6,
      8
    )}T${jobRun.slice(8, 10)}:${jobRun.slice(10, 12)}:00Z`
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatSnapshotTimestamp(jobRun?: string | null): string {
  // job_run is YYYYmmddHHMMSS (UTC) from the Glue job
  const date = snapshotTimestampToDate(jobRun);
  if (!date) return jobRun || "unknown";
  return date.toLocaleString();
}

/**
 * The DLQ snapshot a case's live numbers (owned items, impact, trend) come from. The hourly
 * reconcile appends one trend point per snapshot, stamped with that snapshot's time, while
 * `population.snapshotTimestamp` is only the snapshot the manifest was frozen from at creation
 * (or last refresh) — reading that made a case reconciled an hour ago say "Snapshot 2 days ago".
 * The newer of the two wins, so a refresh after the last reconcile still counts.
 */
export function latestSnapshotDate(investigation: {
  trend?: { t?: string | null }[] | null;
  population?: { snapshotTimestamp?: string | null } | null;
}): Date | null {
  const frozen = snapshotTimestampToDate(
    investigation.population?.snapshotTimestamp
  );
  // Newest parseable point: don't rely on order, or on the last point having a time.
  const reconciledMs = (investigation.trend ?? []).reduce((max, p) => {
    const ms = p?.t ? Date.parse(p.t) : NaN;
    return Number.isNaN(ms) || (!Number.isNaN(max) && ms <= max) ? max : ms;
  }, NaN);
  if (Number.isNaN(reconciledMs)) return frozen;
  if (frozen && frozen.getTime() > reconciledMs) return frozen;
  return new Date(reconciledMs);
}

/**
 * Interval of the worker's `reconcile` task — the `HourlyReconciliation` EventBridge rule in
 * LuscaSearchCDK/lib/backend/investigations.ts (`Schedule.rate(Duration.hours(1))`). Keep in sync.
 */
export const RECONCILIATION_INTERVAL_MS = 60 * 60 * 1000;

/** Above this the rule is matching noticeably more than the pattern's own count. */
export const COVERAGE_WIDER_RATIO = 1.3;
/** Below this the rule is matching noticeably fewer than the pattern's own count. */
export const COVERAGE_NARROWER_RATIO = 0.7;
/** A report window that ended this many days (or more) before the snapshot counts as aged. */
export const COVERAGE_AGED_DAYS = 1;

export interface SuggestionCoverageInput {
  /** Items the rule matches in the current snapshot. */
  count: number;
  /** Items TrackStars validated for the pattern (`totalMatches`). */
  target: number;
  /** The report the pattern came from; its window is what `target` was counted over. */
  report?: Pick<TrackStarsReportSummary, "dateRange" | "generatedAt" | "stale">;
  /** `job_run` of the snapshot `count` was taken from. */
  snapshotTimestamp?: string | null;
  /** True when the pattern still has a constraint the user could pin (a real way to narrow). */
  hasOpenConstraints: boolean;
}

export type SuggestionCoverageVerdict =
  | "close"
  | "wider-pin"
  | "wider-aged"
  | "wider"
  | "narrower-aged"
  | "narrower";

export interface SuggestionCoverage {
  /** Rounded percentage of the pattern's count the rule reaches, or undefined when there is no target. */
  pct?: number;
  verdict: SuggestionCoverageVerdict;
  /** Whole days between the end of the report window and the snapshot, when both are known. */
  ageDays?: number;
  /**
   * One or two sentences: how many items the investigation takes against what TrackStars
   * reported, and — only when there is one — the thing to do about it.
   */
  summary: string;
  /** True when the gap is evidence the rule is wrong (a warning), not just the DLQ moving on. */
  attention: boolean;
}

/**
 * State how a rule derived from a TrackStars pattern compares with what TrackStars reported.
 *
 * The user has already chosen the pattern, so this never questions whether the items are "the
 * same pattern". The two counts come from different windows by construction: `target` is from
 * the report's `dateRange`, `count` from the current hourly snapshot. A recurring pattern keeps
 * growing after the report, so a surplus on an aged report is the normal case and is stated as a
 * fact with nothing to do. A surplus is only flagged when the report is fresh (time cannot
 * explain it) or a constraint is still open to pin (a concrete way to narrow). A shortfall on an
 * aged report means items were redriven or left the DLQ; on a fresh report it means a pin is too
 * specific.
 */
export function describeSuggestionCoverage(
  input: SuggestionCoverageInput
): SuggestionCoverage {
  const { count, target, report, snapshotTimestamp, hasOpenConstraints } =
    input;
  const items = `${count.toLocaleString()} item${count === 1 ? "" : "s"}`;
  if (!target)
    return {
      verdict: "close",
      attention: false,
      summary: `This investigation takes ${items}. TrackStars did not report a count for this pattern.`,
    };
  const ratio = count / target;
  const pct = Math.round(ratio * 100);

  const windowEnd = report?.dateRange?.endDate ?? report?.generatedAt;
  const endDate = windowEnd ? new Date(windowEnd) : null;
  const snapshot = snapshotTimestampToDate(snapshotTimestamp);
  const ageDays =
    endDate && !Number.isNaN(endDate.getTime()) && snapshot
      ? Math.max(
          0,
          Math.floor(
            (snapshot.getTime() - endDate.getTime()) / (24 * 60 * 60 * 1000)
          )
        )
      : undefined;
  const aged =
    report?.stale === true ||
    (ageDays !== undefined && ageDays >= COVERAGE_AGED_DAYS);
  const windowText =
    report?.dateRange?.startDate && report.dateRange.endDate
      ? ` (${report.dateRange.startDate} → ${report.dateRange.endDate})`
      : "";
  const reported = `TrackStars reported ${target.toLocaleString()}${windowText}`;
  const base = { pct, ageDays };

  if (ratio > COVERAGE_WIDER_RATIO) {
    if (hasOpenConstraints) {
      return {
        ...base,
        verdict: "wider-pin",
        attention: true,
        summary: `${items} match, but ${reported}. The pattern needs one more field pinned — see below.`,
      };
    }
    if (aged) {
      return {
        ...base,
        verdict: "wider-aged",
        attention: false,
        summary: `This investigation takes ${items}. ${reported}; the pattern has kept recurring since, so the DLQ holds more now.`,
      };
    }
    return {
      ...base,
      verdict: "wider",
      attention: true,
      summary: `${items} match, but ${reported} over the same period — this rule may be wider than the pattern. Check the population breakdown below for a field to pin.`,
    };
  }
  if (ratio < COVERAGE_NARROWER_RATIO) {
    if (aged) {
      return {
        ...base,
        verdict: "narrower-aged",
        attention: false,
        summary: `This investigation takes ${items}. ${reported}; the rest have since been redriven or left the DLQ.`,
      };
    }
    return {
      ...base,
      verdict: "narrower",
      attention: true,
      summary: `Only ${items} match, but ${reported} over the same period — a pinned value may be too specific.`,
    };
  }
  return {
    ...base,
    verdict: "close",
    attention: false,
    summary: `This investigation takes ${items}, in line with the ${target.toLocaleString()} TrackStars reported${windowText}.`,
  };
}

/**
 * Production Lusca Search. Transaction links always open here, even from a personal dev or
 * other non-prod deployment of this site, so users land on the real tool.
 */
export const LUSCA_SEARCH_PROD_URL =
  "https://lusca-search.prime-video.amazon.dev";

export function luscaSearchPath(
  stage: string,
  transactionOrOrderId: string
): string {
  return `${LUSCA_SEARCH_PROD_URL}/?stage=${encodeURIComponent(
    stage
  )}&transactionId=${encodeURIComponent(transactionOrOrderId)}`;
}

/** MCM page for a change management ID (MCM-123456789). */
export function mcmUrl(mcmId: string): string {
  return `https://mcm.amazon.dev/cms/${encodeURIComponent(mcmId)}`;
}

/** SIM-T page for a ticket ID (V2384450038). */
export function simTicketUrl(ticketId: string): string {
  return `https://t.corp.amazon.com/${encodeURIComponent(ticketId)}`;
}

/** Taskei page for a task or SIM issue ID (TRACK-11420). */
export function taskeiTaskUrl(taskId: string): string {
  return `https://taskei.amazon.dev/tasks/${encodeURIComponent(taskId)}`;
}

/** Standalone work-queue page (the full-width counterpart of the dashboard drawer). */
export function investigationsPagePath(stage: string): string {
  return `/dashboard/dlq/investigations?stage=${stage}`;
}

/**
 * Standalone investigations page opened on the transaction lookup for `transactionId` (AUID or
 * order ID) — the "Investigate" entry point from a transaction's DLQ deep dive.
 */
export function investigateTransactionPath(
  stage: string,
  transactionId: string
): string {
  return `/dashboard/dlq/investigations?stage=${stage}&investigate=${encodeURIComponent(
    transactionId
  )}`;
}

/**
 * Sections of the investigations area, each its own page (see InvestigationsSideNav). `mine` and
 * `unowned` are legacy: they used to be pages of their own and now redirect to the active table
 * with the matching filter preset, and `withdrawn` is the old name of `cancelled` (see
 * LEGACY_SECTION_REDIRECTS), so old links keep working.
 */
export const INVESTIGATION_SECTIONS = [
  "overview",
  "mine",
  "active",
  "unowned",
  "resolved",
  "cancelled",
  "withdrawn",
  "all",
  "suggestions",
  "backlog",
  "lookup",
  "activity",
] as const;
export type InvestigationSectionName = (typeof INVESTIGATION_SECTIONS)[number];

export const isInvestigationSection = (
  value: string | undefined
): value is InvestigationSectionName =>
  !!value && (INVESTIGATION_SECTIONS as readonly string[]).includes(value);

/**
 * Path of a section page; the overview is the area's root. A filter (the two tables' PropertyFilter
 * query, same JSON the backlog API takes) travels as `filter=` so a filtered table is a plain link.
 */
export function sectionPath(
  section: InvestigationSectionName,
  stage: string,
  filter?: GroupsFilterQuery
): string {
  const base = "/dashboard/dlq/investigations";
  const path = `${
    section === "overview" ? base : `${base}/${section}`
  }?stage=${stage}`;
  const serialized = serializeGroupsFilter(filter);
  return serialized ? `${path}&filter=${encodeURIComponent(serialized)}` : path;
}

/**
 * Shareable link straight to the "not yet investigated" table on the standalone page, optionally
 * with a filter pre-applied.
 */
export function backlogPagePath(
  stage: string,
  filter?: GroupsFilterQuery
): string {
  return sectionPath("backlog", stage, filter);
}

/** A one-click slice of a table: a name and the filter it applies. */
export interface FilterPreset<Id extends string = string> {
  id: Id;
  text: string;
  filter: GroupsFilterQuery;
}

/**
 * The active-investigations table's slices, offered under "Active investigations" in the side
 * navigation. Ownership is viewer-relative ("Mine"), so the link is the same for everyone.
 */
export type RegistryPresetId =
  | "mine"
  | "unowned"
  | "following"
  | "ready"
  | "stale";
export const REGISTRY_PRESETS: FilterPreset<RegistryPresetId>[] = [
  {
    id: "mine",
    text: "Mine",
    filter: {
      operation: "and",
      tokens: [{ propertyKey: "ownership", operator: "=", value: "Mine" }],
    },
  },
  {
    id: "unowned",
    text: NEEDS_OWNER_LABEL,
    filter: {
      operation: "and",
      tokens: [
        { propertyKey: "ownership", operator: "=", value: NEEDS_OWNER_LABEL },
      ],
    },
  },
  {
    // Cases the signed-in user follows (Follow button on the case page).
    id: "following",
    text: "Following",
    filter: {
      operation: "and",
      tokens: [{ propertyKey: "following", operator: "=", value: "Yes" }],
    },
  },
  {
    // readyToResolve.READY_TO_RESOLVE: monitored redrives that stayed quiet.
    id: "ready",
    text: "Ready to resolve",
    filter: {
      operation: "and",
      tokens: [
        { propertyKey: "status", operator: "=", value: "READY_TO_RESOLVE" },
      ],
    },
  },
  {
    // Staleness is shown here (and on the case page), not as a marker on every table row.
    id: "stale",
    text: "Stale",
    filter: {
      operation: "and",
      tokens: [{ propertyKey: "stale", operator: "=", value: "Yes" }],
    },
  },
];

/**
 * The active table narrowed to exactly the cases counted by "N need your attention" (see
 * needsAttention). Not a sidebar preset: it is where that flag lands.
 */
export const NEEDS_ATTENTION_FILTER: GroupsFilterQuery = {
  operation: "and",
  tokens: [{ propertyKey: "attention", operator: "=", value: "Yes" }],
};

export interface LegacySectionRedirect {
  to: InvestigationSectionName;
  /** The filter the old page implied; absent keeps whatever filter the URL carries. */
  filter?: GroupsFilterQuery;
}

/**
 * Retired section addresses and where they live now, so old links keep working: `mine` and
 * `unowned` are presets on the active table, `withdrawn` is the old name of `cancelled`.
 */
export const LEGACY_SECTION_REDIRECTS: Partial<
  Record<InvestigationSectionName, LegacySectionRedirect>
> = {
  mine: { to: "active", filter: REGISTRY_PRESETS[0].filter },
  unowned: { to: "active", filter: REGISTRY_PRESETS[1].filter },
  withdrawn: { to: "cancelled" },
};

/** The preset (if any) whose filter is exactly the one in force. */
export function matchingPreset<P extends FilterPreset>(
  presets: readonly P[],
  filter: GroupsFilterQuery | undefined
): P | undefined {
  const current = serializeGroupsFilter(filter);
  return current
    ? presets.find((p) => serializeGroupsFilter(p.filter) === current)
    : undefined;
}

/** A sidebar shortcut into the backlog. */
export type BacklogPreset = FilterPreset<
  "uncategorized" | "categorized" | "high-impact" | "today" | "growing"
>;

/** Label of the backlog slice of populations that grew over the last 7 days. */
export const GROWING_PRESET_TEXT = "Growing fastest";

/** Today's date in the snapshot's own clock (UTC), as the `lastSeen` filter expects it. */
export const todayUtc = (now: Date = new Date()): string =>
  now.toISOString().slice(0, 10);

/**
 * The backlog slices worth one click: groups nobody has categorised, groups that already have a
 * category, groups worth more than the high-impact threshold, groups that failed again today, and
 * groups that grew over the last 7 days of snapshots.
 * `now` is injectable for tests.
 */
export function backlogPresets(now: Date = new Date()): BacklogPreset[] {
  return [
    {
      id: "uncategorized",
      text: "Uncategorized",
      filter: {
        operation: "and",
        tokens: [
          { propertyKey: "category", operator: "=", value: "uncategorized" },
        ],
      },
    },
    {
      id: "categorized",
      text: "Categorized",
      filter: {
        operation: "and",
        tokens: [
          { propertyKey: "category", operator: "!=", value: "uncategorized" },
        ],
      },
    },
    {
      id: "high-impact",
      text: `Over ${new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 0,
      }).format(HIGH_IMPACT_USD)}`,
      filter: {
        operation: "and",
        tokens: [
          {
            propertyKey: "impactUsd",
            operator: ">=",
            value: String(HIGH_IMPACT_USD),
          },
        ],
      },
    },
    {
      id: "today",
      text: "Errored today",
      filter: {
        operation: "and",
        tokens: [
          { propertyKey: "lastSeen", operator: ">=", value: todayUtc(now) },
        ],
      },
    },
    {
      // The backlog table ranks this slice by growth (see InvestigationBacklog).
      id: "growing",
      text: GROWING_PRESET_TEXT,
      filter: {
        operation: "and",
        tokens: [{ propertyKey: "growth", operator: ">", value: "0" }],
      },
    },
  ];
}

export function investigationPath(
  investigationId: string,
  stage: string
): string {
  return `/dashboard/dlq/investigations/${investigationId}?stage=${stage}`;
}

/**
 * Column metadata in a CALLBACK_OPERATION_INVOKED datapoint is nested by role and type:
 * `{ Field | Metric | Dimension | Measure: { String | Integer | Decimal | DateTime | Calculated | Bin:
 * { Column: { ColumnName, DataSetIdentifier } } } }`. Older payloads carried `{ Column: { Name } }`
 * or `{ Name }`. Returns the column name, or "" when none can be found.
 */
export function quickSightColumnName(column: unknown): string {
  const readColumn = (holder: unknown): string | undefined => {
    const col = (holder as { Column?: { ColumnName?: string; Name?: string } })
      ?.Column;
    return col?.ColumnName || col?.Name;
  };
  if (!column || typeof column !== "object") return "";
  const direct =
    readColumn(column) || (column as { Name?: string }).Name || undefined;
  if (direct) return direct;
  // Role wrapper (Field/Metric/Dimension/Measure) → type wrapper → Column.
  for (const role of Object.values(column as Record<string, unknown>)) {
    if (!role || typeof role !== "object") continue;
    const fromRole = readColumn(role);
    if (fromRole) return fromRole;
    for (const typed of Object.values(role as Record<string, unknown>)) {
      const fromType = readColumn(typed);
      if (fromType) return fromType;
    }
  }
  return "";
}

/**
 * True when the datapoint column is an aggregate (Metric / Measure / Calculated), whose value is a
 * count or sum rather than the row's own value for that column.
 */
function isQuickSightMetricColumn(column: unknown): boolean {
  if (!column || typeof column !== "object") return false;
  const c = column as Record<string, unknown>;
  if (c.Metric || c.Measure) return true;
  const field = (c.Field || c.Dimension) as Record<string, unknown> | undefined;
  return !!field && ("Calculated" in field || "Bin" in field);
}

/**
 * Flattens a CALLBACK_OPERATION_INVOKED message into lower-cased column name → display value for
 * the first datapoint. Raw values are preferred; the formatted value is the fallback. A table can
 * carry both a dimension and a metric over the same column (e.g. `error_category` plus a hidden
 * `count(error_category)`); the dimension's value wins so a rule never gets seeded with a count.
 */
export function parseQuickSightCallbackValues(
  message: unknown
): Record<string, string> {
  const payload = message as {
    Datapoints?: Array<{
      Columns?: unknown[];
      RawValues?: Array<{
        String?: string;
        Integer?: number;
        Decimal?: number;
        Boolean?: boolean;
        Date?: string | Date;
        Null?: boolean;
      }>;
      FormattedValues?: Array<{ Value?: string }>;
    }>;
  };
  const datapoint = payload?.Datapoints?.[0];
  if (!datapoint?.Columns) return {};
  const values: Record<string, string> = {};
  const fromMetric = new Set<string>();
  datapoint.Columns.forEach((column, index) => {
    const name = quickSightColumnName(column).toLowerCase();
    if (!name) return;
    const metric = isQuickSightMetricColumn(column);
    // Never let an aggregate overwrite a dimension value of the same name.
    if (name in values && (metric || !fromMetric.has(name))) return;
    const raw = datapoint.RawValues?.[index];
    let value: string | undefined;
    if (raw) {
      if (raw.String !== undefined) value = raw.String;
      else if (raw.Integer !== undefined) value = String(raw.Integer);
      else if (raw.Decimal !== undefined) value = String(raw.Decimal);
      else if (raw.Boolean !== undefined) value = String(raw.Boolean);
      else if (raw.Date !== undefined)
        value =
          raw.Date instanceof Date ? raw.Date.toISOString() : String(raw.Date);
    }
    if (value === undefined) value = datapoint.FormattedValues?.[index]?.Value;
    if (value === undefined) return;
    values[name] = value;
    if (metric) fromMetric.add(name);
    else fromMetric.delete(name);
  });
  return values;
}

/**
 * Parses a QuickSight CALLBACK_OPERATION_INVOKED message into a row seed. Returns null when the
 * visual carries no `dlq_pk` column — see `parseQuickSightCallbackTransactionId` for the fallback.
 */
export function parseQuickSightCallback(message: unknown): RowSeed | null {
  const values = parseQuickSightCallbackValues(message);
  const dlqPk = values.dlq_pk || values.pk;
  if (!dlqPk) return null;
  return {
    dlqPk,
    eventsourcekey: values.eventsourcekey,
    error_type: values.error_type,
    error_message: values.error_message,
    error_category: values.error_category,
    gl_product_group: values.gl_product_group,
    marketplace_id: values.marketplace_id,
    usecase: values.usecase,
    digital_order_source: values.digital_order_source,
    is_emp_order: values.is_emp_order,
    destination: values.destination,
    accounting_operation: values.accounting_operation,
    payment_processor: values.payment_processor,
    exception_fingerprint: values.exception_fingerprint,
    flare_error_types: values.flare_error_types,
    flare_invalid_values: values.flare_invalid_values,
    flare_missing_attributes: values.flare_missing_attributes,
    flare_failed_schema_definitions: values.flare_failed_schema_definitions,
    flare_attributes_not_covered: values.flare_attributes_not_covered,
    flare_balance_scope: values.flare_balance_scope,
    flare_balance_scope_value: values.flare_balance_scope_value,
    transaction_id: values.transaction_id,
    order_id: values.order_id,
  };
}

/**
 * Fallback for row visuals that show transaction/order IDs but no `dlq_pk`: the ID to run the
 * transaction lookup with (`transaction_id`, then `order_id`), or null.
 */
export function parseQuickSightCallbackTransactionId(
  message: unknown
): string | null {
  const values = parseQuickSightCallbackValues(message);
  const id = (values.transaction_id || values.order_id || "").trim();
  return id || null;
}

/** Human label for a rule field (falls back to the raw field name). */
export function ruleFieldLabel(field: string): string {
  return RULE_FIELDS.find((f) => f.field === field)?.label || field;
}

/**
 * AWS console deeplink for an investigation manifest (object view for a key, bucket listing for
 * a prefix). Population and redrive-attempt manifests both live in the Lusca Search service's own
 * `luscasearch-investigations-<account>-<stage>` bucket (LuscaSearchCDK investigations.ts), not
 * in the PV Accounting data account — so this is a plain console link, deliberately not the
 * Merlon-wrapped `generateS3ObjectUrl` / `generateS3PrefixUrl` (helpers/logUtils), which sign in
 * to the data account's ops role and are right for the DLQ redrive bucket instead.
 */
export function s3ConsoleUrl(s3Uri?: string | null): string | null {
  if (!s3Uri || !s3Uri.startsWith("s3://")) return null;
  const [bucket, ...rest] = s3Uri.slice("s3://".length).split("/");
  if (!bucket) return null;
  const key = rest.join("/");
  const region = "us-east-1";
  const view = !key || key.endsWith("/") ? "buckets" : "object";
  return `https://${region}.console.aws.amazon.com/s3/${view}/${encodeURIComponent(
    bucket
  )}?region=${region}&prefix=${encodeURIComponent(key)}`;
}

/**
 * "Items assigned" is the number of DLQ items this investigation exclusively owns, which is
 * different from the frozen population count: items already owned by another investigation
 * stay with it, and for large populations ownership is claimed asynchronously by the
 * worker, so the count reads 0 until that finishes.
 */
export const assignedSummary = (investigation: Investigation): string => {
  const assigned = investigation.assignedCount ?? 0;
  const total = investigation.population?.count ?? 0;
  if (investigation.assignment?.status === "PENDING") {
    return `${assigned} of ${total} · assigning in background, refresh to update`;
  }
  return `${assigned}`;
};

/**
 * One-line size headline for the investigation page: the items this case owns, with the gap to
 * the frozen snapshot explained only when there is one. Normally every matched item is owned
 * and this reads just "1,118 items".
 */
export const populationSummary = (investigation: Investigation): string => {
  const total = investigation.population?.count ?? 0;
  const items = (n: number) =>
    `${n.toLocaleString()} ${n === 1 ? "item" : "items"}`;
  if (investigation.assignment?.status === "PENDING") {
    return `${items(total)} · assigning ownership…`;
  }
  const assigned = investigation.assignedCount ?? total;
  if (isTerminal(investigation.status)) {
    // Closed cases release their items, so the owned count is 0 by design: show the snapshot.
    return items(total);
  }
  const parts = [items(assigned)];
  const elsewhere = total - assigned;
  if (elsewhere > 0) {
    // Owned by an overlapping case at creation, or moved to a split-off child since.
    parts.push(
      `${elsewhere.toLocaleString()} more matched, owned by other cases`
    );
  }
  return parts.join(" · ");
};

/**
 * Short relative time for glanceable metadata ("just now", "12 min ago", "3 h ago",
 * "yesterday", "4 days ago", then a date). Pair it with the full timestamp in a tooltip.
 */
export const formatRelativeTime = (
  iso?: string | null,
  now: number = Date.now()
): string => {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";
  const minutes = Math.round((now - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(then).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

/**
 * The overview's one metadata line: snapshot age, creator, last human activity. Relative values
 * for scanning, full timestamps for the hover tooltip. Last activity is dropped when it is just
 * the creation.
 */
export const overviewMeta = (
  investigation: Investigation,
  now: number = Date.now()
): { label: string; value: string; full: string }[] => {
  const snapshot = latestSnapshotDate(investigation);
  const meta = [
    {
      label: "Snapshot",
      value: formatRelativeTime(snapshot?.toISOString(), now),
      full: snapshot ? snapshot.toLocaleString() : "unknown",
    },
    {
      label: `Created by ${investigation.createdBy ?? "unknown"}`,
      value: formatRelativeTime(investigation.createdAt, now),
      full: formatTimestamp(investigation.createdAt),
    },
  ];
  const last = investigation.lastHumanActivityAt;
  if (last && last !== investigation.createdAt) {
    meta.push({
      label: "Last activity",
      value: formatRelativeTime(last, now),
      full: formatTimestamp(last),
    });
  }
  return meta;
};

/** Who owns a newly created investigation: the creator, nobody, or a named Lusca Search user. */
export type CreateOwnership = "me" | "nobody" | { owner: string };

/** The create-request fields for an ownership choice (an explicit owner wins over claim). */
export const ownershipRequestFields = (
  ownership: CreateOwnership
): { claim: boolean; owner?: string } =>
  typeof ownership === "object"
    ? { claim: false, owner: ownership.owner }
    : { claim: ownership === "me" };

/** What the drawer needs to know about a create result, whichever form produced it. */
export interface CreatedSummary {
  assignedCount: number;
  overlappingCount: number;
  conflictedCount: number;
  assignmentPending: boolean;
  /** Set when the claim won nothing and the new case was closed at once as a duplicate of this one. */
  duplicateOfInvestigationId?: string;
}

export const createdSummary = (result: {
  assignedCount?: number;
  overlappingCount?: number;
  conflictedCount?: number;
  assignmentPending?: boolean;
  duplicateOfInvestigationId?: string;
}): CreatedSummary => ({
  assignedCount: result.assignedCount ?? 0,
  overlappingCount: result.overlappingCount ?? 0,
  conflictedCount: result.conflictedCount ?? 0,
  assignmentPending: !!result.assignmentPending,
  duplicateOfInvestigationId: result.duplicateOfInvestigationId || undefined,
});
